import React, { useState } from 'react';
import { useErrorLogs, useErrorStats, ErrorFilters } from '@/hooks/useAdminErrors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertCircle,
  AlertTriangle,
  Info,
  XCircle,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { ErrorDetailsModal } from '@/components/admin/ErrorDetailsModal';
import { ErrorStatsCharts } from '@/components/admin/ErrorStatsCharts';
import { formatDistanceToNow } from 'date-fns';

const AdminErrorsPage = () => {
  const [filters, setFilters] = useState<ErrorFilters>({
    page: 1,
    page_size: 50,
  });
  const [selectedErrorId, setSelectedErrorId] = useState<string | undefined>();
  const [showFilters, setShowFilters] = useState(false);

  const { data: errorData, isLoading, refetch } = useErrorLogs(filters);
  const { data: stats } = useErrorStats();

  const handleFilterChange = (key: keyof ErrorFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : prev.page, // Reset to page 1 when changing filters
    }));
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSeverityBadge = (severity: string) => {
    const variants: Record<string, 'destructive' | 'default' | 'secondary' | 'outline'> = {
      critical: 'destructive',
      error: 'destructive',
      warning: 'default',
      info: 'secondary',
    };
    return (
      <Badge variant={variants[severity] || 'outline'}>
        {severity.toUpperCase()}
      </Badge>
    );
  };

  const getTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      frontend: 'bg-blue-100 text-blue-800',
      backend: 'bg-purple-100 text-purple-800',
      api: 'bg-green-100 text-green-800',
      celery: 'bg-orange-100 text-orange-800',
    };
    return (
      <Badge variant="outline" className={colors[type] || ''}>
        {type}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Error Logs</h1>
          <p className="text-muted-foreground">
            Monitor and manage application errors
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Charts */}
      {stats && <ErrorStatsCharts stats={stats} />}

      {/* Filters */}
      <div className="bg-card rounded-lg border p-4 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide' : 'Show'}
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="col-span-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search messages and error codes..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Error Type */}
            <Select
              value={filters.error_type || 'all'}
              onValueChange={(value) =>
                handleFilterChange('error_type', value === 'all' ? undefined : value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Error Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="frontend">Frontend</SelectItem>
                <SelectItem value="backend">Backend</SelectItem>
                <SelectItem value="api">API</SelectItem>
                <SelectItem value="celery">Celery</SelectItem>
              </SelectContent>
            </Select>

            {/* Severity */}
            <Select
              value={filters.severity || 'all'}
              onValueChange={(value) =>
                handleFilterChange('severity', value === 'all' ? undefined : value)
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="info">Info</SelectItem>
              </SelectContent>
            </Select>

            {/* Resolution Status */}
            <Select
              value={
                filters.resolved === undefined
                  ? 'all'
                  : filters.resolved
                  ? 'resolved'
                  : 'unresolved'
              }
              onValueChange={(value) =>
                handleFilterChange(
                  'resolved',
                  value === 'all' ? undefined : value === 'resolved'
                )
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unresolved">Unresolved</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
              </SelectContent>
            </Select>

            {/* Page Size */}
            <Select
              value={String(filters.page_size || 50)}
              onValueChange={(value) =>
                handleFilterChange('page_size', parseInt(value))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Page Size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {/* Error Table */}
      <div className="bg-card rounded-lg border">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading errors...</p>
          </div>
        ) : errorData && errorData.errors.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead className="max-w-md">Message</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Count</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {errorData.errors.map((error) => (
                  <TableRow
                    key={error.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedErrorId(error.id)}
                  >
                    <TableCell>{getSeverityIcon(error.severity)}</TableCell>
                    <TableCell>{getTypeBadge(error.error_type)}</TableCell>
                    <TableCell>{getSeverityBadge(error.severity)}</TableCell>
                    <TableCell className="max-w-md">
                      <div className="truncate" title={error.message}>
                        {error.message}
                      </div>
                      {error.error_code && (
                        <div className="text-xs text-muted-foreground">
                          {error.error_code}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {error.user_email ? (
                        <span className="text-sm">{error.user_email}</span>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          Anonymous
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{error.occurrence_count}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(error.last_seen), {
                        addSuffix: true,
                      })}
                    </TableCell>
                    <TableCell>
                      {error.resolved ? (
                        <Badge variant="secondary">Resolved</Badge>
                      ) : (
                        <Badge variant="outline">Open</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="border-t px-4 py-3 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Showing {(errorData.page - 1) * errorData.page_size + 1} to{' '}
                {Math.min(errorData.page * errorData.page_size, errorData.total)} of{' '}
                {errorData.total} errors
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFilterChange('page', filters.page! - 1)}
                  disabled={filters.page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-2 px-3 text-sm">
                  Page {errorData.page} of {errorData.pages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleFilterChange('page', filters.page! + 1)}
                  disabled={filters.page === errorData.pages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No errors found</h3>
            <p className="text-muted-foreground">
              No errors match your current filters.
            </p>
          </div>
        )}
      </div>

      {/* Error Details Modal */}
      {selectedErrorId && (
        <ErrorDetailsModal
          errorId={selectedErrorId}
          onClose={() => setSelectedErrorId(undefined)}
        />
      )}
    </div>
  );
};

export default AdminErrorsPage;
